import React, { useState, useEffect, useRef } from 'react';
import { Upload, Search, Trash2, Image, FileText, Music, Video, Folder, Grid, List } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMedia = () => {
  const { token } = useAuth();
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [folderFilter, setFolderFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const fileRef = useRef(null);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 48 });
      if (folderFilter) params.set('folder', folderFilter);
      if (typeFilter) params.set('file_type', typeFilter);
      if (search) params.set('search', search);
      const res = await fetch(`${API_URL}/api/media/?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setAssets(data.assets);
        setFolders(data.folders || []);
        setTotal(data.total);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchAssets(); }, [folderFilter, typeFilter]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.[^.]+$/, ''));
      fd.append('folder', folderFilter || 'general');
      try {
        await fetch(`${API_URL}/api/media/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      } catch (e) { console.error(e); }
    }
    
    setUploading(false);
    fetchAssets();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    await fetch(`${API_URL}/api/media/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchAssets();
  };

  const typeIcons = { image: Image, document: FileText, audio: Music, video: Video };

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Media Library</h1>
          <p className="text-cream/30 text-sm mt-1">{total} assets</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" multiple accept="image/*,audio/*,video/*,.pdf" onChange={handleUpload} className="hidden" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-navy text-sm font-semibold rounded-lg hover:bg-gold-light transition-all disabled:opacity-50" data-testid="upload-btn">
            <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex gap-1 bg-navy border border-gold/[0.06] rounded-lg p-1">
          {['', 'image', 'audio', 'document', 'video'].map((t) => (
            <button key={t} onClick={() => setTypeFilter(t)} className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${typeFilter === t ? 'bg-gold/[0.1] text-gold' : 'text-cream/30 hover:text-cream/50'}`}>
              {t || 'All'}
            </button>
          ))}
        </div>
        {folders.length > 0 && (
          <select value={folderFilter} onChange={(e) => setFolderFilter(e.target.value)} className="bg-navy border border-gold/[0.06] rounded-lg px-3 py-1.5 text-xs text-cream">
            <option value="">All Folders</option>
            {folders.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        )}
        <div className="flex gap-1 ml-auto">
          <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded ${viewMode === 'grid' ? 'text-gold' : 'text-cream/20'}`}><Grid size={16} /></button>
          <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'text-gold' : 'text-cream/20'}`}><List size={16} /></button>
        </div>
      </div>

      {/* Assets */}
      {loading ? (
        <div className="py-16 text-center text-cream/20 text-sm">Loading...</div>
      ) : assets.length === 0 ? (
        <div className="py-16 text-center bg-navy border border-dashed border-gold/[0.06] rounded-xl">
          <Upload size={32} className="mx-auto text-cream/10 mb-3" />
          <p className="text-cream/30 text-sm">No media assets</p>
          <button onClick={() => fileRef.current?.click()} className="text-gold text-sm mt-2 hover:text-gold-light">Upload your first file</button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {assets.map((a) => {
            const TypeIcon = typeIcons[a.file_type] || FileText;
            return (
              <div key={a.id} className="group bg-navy border border-gold/[0.04] rounded-lg overflow-hidden hover:border-gold/15 transition-all relative">
                <div className="aspect-square bg-navy-deep flex items-center justify-center">
                  {a.file_type === 'image' ? (
                    <img src={`${API_URL}${a.url}`} alt={a.alt_text || a.title} className="w-full h-full object-cover" />
                  ) : (
                    <TypeIcon size={24} className="text-cream/15" />
                  )}
                </div>
                <div className="p-2">
                  <p className="text-cream/50 text-[10px] truncate">{a.title}</p>
                  <p className="text-cream/15 text-[9px]">{(a.size / 1024).toFixed(0)}KB</p>
                </div>
                <button onClick={() => handleDelete(a.id)} className="absolute top-1 right-1 p-1 bg-black/60 rounded opacity-0 group-hover:opacity-100 text-red-400 transition-all"><Trash2 size={10} /></button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {assets.map((a) => (
                <tr key={a.id} className="border-b border-gold/[0.03] hover:bg-cream/[0.01]">
                  <td className="px-4 py-2 w-10">
                    {a.file_type === 'image' ? (
                      <img src={`${API_URL}${a.thumbnail_url || a.url}`} alt="" className="w-8 h-8 rounded object-cover" />
                    ) : (
                      <div className="w-8 h-8 bg-navy-deep rounded flex items-center justify-center"><FileText size={12} className="text-cream/20" /></div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-cream/60">{a.title}</td>
                  <td className="px-4 py-2 text-cream/20 text-xs">{a.file_type}</td>
                  <td className="px-4 py-2 text-cream/15 text-xs">{(a.size / 1024).toFixed(0)}KB</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => handleDelete(a.id)} className="p-1 text-cream/20 hover:text-red-400 transition-colors"><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
